import Router from 'next/router';
import { useEffect } from 'react';

import { paths } from '@/lib/paths';
import { StorageHelper } from '@/lib/storage';

const LIVE_BUILD_ID_TTL_MS = 60_000;
// A hung check is dropped and the last known id is kept; nothing waits on it.
const LIVE_BUILD_ID_TIMEOUT_MS = 3_000;
const RELOADED_FOR_BUILD_ID_KEY = 'buildIdReloadedFor';

const readReloadedFor = (storage: StorageHelper): string | null => {
  try {
    // StorageHelper JSON-parses on read, so ids like "25" come back as numbers.
    const value = storage.getSessionStorage(RELOADED_FOR_BUILD_ID_KEY);
    return value == null ? null : String(value);
  } catch {
    return null;
  }
};

const writeReloadedFor = (storage: StorageHelper, buildId: string) => {
  try {
    storage.setSessionStorage(RELOADED_FOR_BUILD_ID_KEY, buildId);
  } catch {
    // Storage blocked: the reload itself still picks up the new build.
  }
};

const fetchLiveBuildId = async (url: string): Promise<string | null> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LIVE_BUILD_ID_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      cache: 'no-store',
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const { buildId } = await response.json();
    return typeof buildId === 'string' && buildId ? buildId : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
};

/** BEC-25988: after a deploy, old tabs 404 on /_next/data/<old id>; once a newer live id is known, the next navigation becomes a full page load. The check runs in the background and never blocks a navigation. */
export const useBuildIdGuard = () => {
  useEffect(() => {
    const clientBuildId = process.env.NEXT_PUBLIC_BUILD_ID;
    if (!clientBuildId) return;

    const storage = new StorageHelper();
    const buildIdUrl = paths.api.buildId.asPath();
    let liveBuildId: string | null = null;
    // A fresh page is on the live build, so the first check waits one TTL.
    let checkedAt = Date.now();
    let inFlight: Promise<void> | null = null;

    const refreshIfStale = () => {
      if (inFlight || Date.now() - checkedAt <= LIVE_BUILD_ID_TTL_MS) return;
      inFlight = fetchLiveBuildId(buildIdUrl).then((buildId) => {
        checkedAt = Date.now();
        liveBuildId = buildId ?? liveBuildId;
        inFlight = null;
      });
    };

    // Reload at most once per live build id, so stale cached HTML cannot loop.
    const claimReload = () => {
      if (
        !liveBuildId ||
        liveBuildId === clientBuildId ||
        readReloadedFor(storage) === liveBuildId
      ) {
        return false;
      }
      writeReloadedFor(storage, liveBuildId);
      return true;
    };

    // Next carries on with its own navigation; the full load replaces it. Back/forward has already changed the URL, so assigning it reloads in place.
    const handleRouteChangeStart = (
      url: string,
      { shallow }: { shallow: boolean },
    ) => {
      if (shallow) return;
      if (claimReload()) {
        globalThis.location.assign(url);
      } else {
        refreshIfStale();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') refreshIfStale();
    };

    Router.events.on('routeChangeStart', handleRouteChangeStart);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      Router.events.off('routeChangeStart', handleRouteChangeStart);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
};
